// A company's feature ticks, as Noctant's platform staff see and change them.
// Every company has the same tools; a tick says whether the company may open
// new work of a feature. Work already open runs to its end whatever the ticks say.
// A change is made from the ticks shown; when they changed since, the card
// reloads.

import { For, Show, createResource, createSignal } from "solid-js";

import AsyncResource from "./AsyncResource";
import {
  ApiError,
  api,
  failureSentence,
  type CompanyFeature,
  type CompanyFeatures,
} from "../lib/api";

/** Every feature, in the vocabulary's order, with what its tick opens. */
const FEATURES: ReadonlyArray<[CompanyFeature, string]> = [
  ["accounts", "Accounts: provision users, create accounts, mint launch links"],
  ["challenges", "Challenges: templates, products and attaching them"],
  ["resets", "Resets the company requests"],
  ["payouts", "Payout requests the company opens"],
  ["kyc_reports", "Payouts gated on its KYC reports (a report is always taken)"],
  ["historical_replay", "Historical replay and backtest launches"],
  ["webhooks", "New webhook messages; one already created is still delivered"],
  ["simulation_credits", "Simulation credits: settings and grants"],
  ["real_money_settlement", "Templates it settles in real money, outside Noctant"],
  ["in_app_starts", "Runs its traders start themselves in the app"],
  ["own_practice_challenges", "A trader's own practice challenge in the company"],
];

export default function CompanyFeaturesCard(props: { orgId: string }) {
  const [shown, { mutate, refetch }] = createResource(
    () => props.orgId,
    (orgId) => api.getCompanyFeatures(orgId),
  );
  const [ticked, setTicked] = createSignal<ReadonlySet<CompanyFeature> | null>(
    null,
  );
  const [reason, setReason] = createSignal("");
  const [message, setMessage] = createSignal<string | null>(null);

  const tickedShown = (current: CompanyFeatures): ReadonlySet<CompanyFeature> =>
    ticked() ?? new Set(current.features);

  function toggle(current: CompanyFeatures, feature: CompanyFeature, on: boolean) {
    const next = new Set(tickedShown(current));
    if (on) next.add(feature);
    else next.delete(feature);
    setTicked(next);
  }

  async function save(ev: Event, current: CompanyFeatures) {
    ev.preventDefault();
    const next = tickedShown(current);
    const features = FEATURES.map(([feature]) => feature).filter((feature) =>
      next.has(feature),
    );
    try {
      mutate(
        await api.setCompanyFeatures(
          props.orgId,
          current.features,
          features,
          reason(),
        ),
      );
      setTicked(null);
      setReason("");
      setMessage("Saved.");
    } catch (e) {
      if (e instanceof ApiError && e.status === 412) {
        setTicked(null);
        void refetch();
        setMessage("The features changed since they were shown; reloaded.");
        return;
      }
      setMessage(failureSentence(e));
    }
  }

  return (
    <div class="card mb-4">
      <div class="card-header">
        <h2 class="text-sm font-semibold">Company features</h2>
      </div>
      <div class="card-body">
        <AsyncResource resource={shown}>
          {(current) => (
            <form class="space-y-3" onSubmit={(ev) => void save(ev, current())}>
              <p class="text-xs text-slate-600">
                An unticked feature stops new work of it; work already open
                runs to its end. A tick with no door yet stops nothing
                (charter §4).
              </p>
              <For each={FEATURES}>
                {([feature, label]) => (
                  <label class="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={tickedShown(current()).has(feature)}
                      onChange={(e) =>
                        toggle(current(), feature, e.currentTarget.checked)
                      }
                    />
                    <span>
                      <span class="font-mono text-xs">{feature}</span> — {label}
                    </span>
                  </label>
                )}
              </For>
              <div>
                <label class="label" for="company-features-reason">
                  Reason
                </label>
                <input
                  id="company-features-reason"
                  class="input"
                  required
                  value={reason()}
                  onInput={(e) => setReason(e.currentTarget.value)}
                />
              </div>
              <Show when={message()}>
                <p class="text-sm text-slate-700">{message()}</p>
              </Show>
              <button type="submit" class="btn-primary">
                Save company features
              </button>
            </form>
          )}
        </AsyncResource>
      </div>
    </div>
  );
}
